"use client";
import React from "react";
import Table, { ColumnDef } from "@/components/primitives/table";
import { BettorHistoricalPick, BettorHistoricalPicks, HistoricalPick, LineType, OddOutcome, PaginatedResponse, PickType, Sport, formatSport, getPickAlignment, pickToOdd, pickTypeToLineType } from "common";
import TeamLogo from "../team/logo";
import { formatDiff, formatDate, formatOdd } from "@/utils";
import Icon from "../primitives/icon";
import { empty } from "@/utils";
import { createGameDetailSlug } from "@/utils/url";
import TablePagination, { TablePaginationProps } from "../primitives/table-pagination";
import GameTableVersus from "../game/table/versus";
import { useOddsFormat } from "@/utils/providers/odds-format";

export default function PickHistoryTable({
  data,
  fallback,
  error,
  pagination,
}: {
  data?: HistoricalPick[];
  fallback?: React.ReactNode;
  error?: boolean;
  pagination: TablePaginationProps;
}) {
  const { oddsFormat } = useOddsFormat();

  // const [processedData, setProcessedData] = React.useState(data ? convertTime(data.data) : []);
  
  // function convertTime(data: BettorHistoricalPick[]): BettorHistoricalPick[] {
  //   return data.map((row) => ({
  //     ...row,
  //     dateTime: row.game.dateTime
  //       ? formatDate(row.game.dateTime, true, true, true)
  //       : "N/A",
  //   }));
  // }

  // React.useEffect(() => {
  //   if (data) {
  //     setProcessedData(convertTime(data.data));
  //   }
  // }, [data, oddsFormat]);

  const columns: ColumnDef<HistoricalPick>[] = React.useMemo(
    () => [
      {
        header: "date",
        size: 120,
        sortingEnabled: false,

        accessorFn: (info) => {
          return formatDate(info.game.dateTime, true, true, true);
        }
      },
      {
        header: "game",
        size: 200,
        sortingEnabled: false,

        cell: (info) => {
          const row = info.row.original;
          return (
            <GameTableVersus
              teams={{ away: row.game.teams[0], home: row.game.teams[1] }}
              slug={createGameDetailSlug(formatSport(row.pick.sportType) as Sport, row.game.id)}
            />
          );
        },
      },
      {
        header: "pick",
        size: 160,
        sortingEnabled: false,

        cell: (info) => {
          const row = info.row.original;
          const formattedOdd = formatOdd(oddsFormat, pickToOdd(row.pick));
          const lineType = pickTypeToLineType(row.pick.type);
          const alignment = getPickAlignment(row.pick.type);
          return (
            <div className="flex items-center gap-2">
              <span className="text-title-sm-bold">
                {row.game.status === "Postponed" ? (
                  <span className="text-secondary-300">PP</span>
                ) : row.game.status === "Canceled" ? (
                  <span className="text-secondary-300">C</span>
                ) : row.pick.outcome === OddOutcome.Win ? (
                  <span className="text-success-300">W</span>
                ) : row.pick.outcome === OddOutcome.Loss ? (
                  <span className="text-error-300">L</span>
                ) : (
                  <span className="text-orange">P</span>
                )}
              </span>
              {(lineType !== LineType.Total) && (
                <TeamLogo
                  team={
                    alignment === 0
                      ? row.game.teams[0]
                      : row.game.teams[1]
                  }
                  size="xs"
                />
              )}
              {row.pick.type === PickType.Over && (
                <div className="flex items-center gap-1">
                  <Icon
                    name="arrow-up"
                    className="fill-primary-400"
                    size="xs"
                  />
                  <div className="text-label-lg-bold text-secondary-300 uppercase">
                    Over
                  </div>
                </div>
              )}
              {row.pick.type === PickType.Under && (
                <div className="flex items-center gap-1">
                  <Icon name="arrow-down" className="fill-hot" size="xs" />
                  <div className="text-label-lg-bold text-secondary-300 uppercase">
                    Under
                  </div>
                </div>
              )}
              <span>{lineType === LineType.Moneyline ? formattedOdd?.payout : formattedOdd?.value}</span>
            </div>
          );
        },
      },
      {
        header: "units",
        accessorFn: (info) => info.pick.unitsGained,
        size: 160,
        sortingEnabled: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex gap-0.5 text-secondary-300 justify-center items-center text-center">
              {row.pick.unitsGained > 0 ? (
                <>
                  <Icon
                    name="carom-up"
                    className="fill-success-300"
                    size="sm"
                  />
                  <div className="flex gap-1 items-center">
                    <span className="text-title-medium">
                      {formatDiff(row.pick.unitsGained)}
                    </span>
                    <span className="text-title-sm">units</span>
                  </div>
                </>
              ) : row.pick.unitsGained < 0 ? (
                <>
                  <Icon
                    name="carom-down"
                    className="fill-error-300"
                    size="sm"
                  />
                  <div className="flex gap-1 items-center">
                    <span className="text-title-medium">
                      {formatDiff(row.pick.unitsGained)}
                    </span>
                    <span className="text-title-sm">units</span>
                  </div>
                </>
              ) : (
                empty()
              )}
              {/* <div className="flex gap-1 items-center">
                <span className="text-title-medium">
                  {formatDiff(row.unitDiff)}
                </span>
                <span className="text-title-sm">units</span>
              </div> */}
            </div>
          );
        },
      },
    ],
    [oddsFormat]
  );
  return (
    <Table
      columns={columns}
      data={data}
      type="banded"
      // initialSort={{column: "date", direction: "desc"}}
      fallback={fallback}
      error={error}
      footer={ <TablePagination
        page={pagination.page}
        size={pagination.size}
        count={pagination.count}
        onPageChange={pagination.onPageChange}
        onPageSizeChange={pagination.onPageSizeChange}
      />}
    />
  );
}
